import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Header from '../../components/Header';
import { toast } from 'react-toastify';
import priceWatchApi from '../../api/priceWatchApi';
import { LinkSource } from '../../constants/link-source';
import resolveBuyLink, { NO_AFF_LINK_MESSAGE } from '../../utils/resolve-buy-link';
import PriceWatchSummaryBar from './price-watch-summary-bar';
import PriceWatchCard from './price-watch-card';
import PriceWatchAlertModal from './price-watch-alert-modal';
import PriceWatchGuideSection from './price-watch-guide-section';

function PriceWatch() {
    const { isLogined } = useSelector((state) => state.auth);

    const [watches, setWatches] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingItem, setEditingItem] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const fetchWatches = useCallback(async () => {
        if (!isLogined) return;
        try {
            setIsLoading(true);
            const res = await priceWatchApi.getMyWatches();
            setWatches(res.data?.data || []);
        } catch (err) {
            console.warn('[PriceWatch] fetch error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [isLogined]);

    useEffect(() => { fetchWatches(); }, [fetchWatches]);

    const handleRemove = async (id) => {
        setWatches((prev) => prev.filter((w) => w.id !== id));
        setConfirmDeleteId(null);
        try {
            await priceWatchApi.removeWatch(id);
        } catch {
            fetchWatches();
        }
    };

    const handleBuy = async (item) => {
        setIsRedirecting(true);
        try {
            const { url, followUpUrl } = await resolveBuyLink({
                platform: item.platform,
                productLink: item.productLink,
                productId: item.itemId,
                meta: {
                    productName: item.productName,
                    productImage: item.imageUrl,
                    productPrice: item.lastKnownPrice,
                },
            }, LinkSource.PriceWatch);
            window.location.href = url;
            if (followUpUrl) setTimeout(() => { window.location.href = followUpUrl; }, 2500);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || NO_AFF_LINK_MESSAGE);
        } finally {
            setIsRedirecting(false);
        }
    };

    const handleEditConfirm = async (config) => {
        if (!editingItem) return;
        try {
            await priceWatchApi.updateWatchAlert(editingItem.id, config);
            setWatches((prev) =>
                prev.map((w) =>
                    w.id === editingItem.id
                        ? { ...w, alertThreshold: config.alertThreshold, alertTargetPrice: config.alertTargetPrice }
                        : w,
                ),
            );
        } catch (err) {
            console.warn('[PriceWatch] update alert error:', err);
        } finally {
            setEditingItem(null);
        }
    };

    return (
        <div className="min-h-dvh bg-surface-muted flex flex-col md:w-[768px] lg:w-[1024px] md:mx-auto">
            {/* Redirecting overlay */}
            {isRedirecting && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
                    <div className="bg-surface rounded-2xl p-6 flex flex-col items-center gap-3 shadow-xl mx-6">
                        <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-semibold text-content-2">Đang tạo link hoàn tiền...</p>
                    </div>
                </div>
            )}

            <Header hasBackButton />

            <div className="pt-20 flex flex-col flex-1 pb-6">
                {/* Summary bar — chỉ hiện khi có item */}
                {watches.length > 0 && <PriceWatchSummaryBar watches={watches} />}

                {/* Content */}
                <div className="flex flex-col gap-2 p-3">
                    {isLoading ? (
                        [1, 2, 3].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-24 animate-pulse" />
                        ))
                    ) : watches.length === 0 ? (
                        <div className="text-center py-10 text-content-3 text-sm px-6">
                            <p className="text-3xl mb-3">🔔</p>
                            <p className="font-semibold text-content-2 mb-1">Chưa theo dõi sản phẩm nào</p>
                            <p>Nhấn 🔔 trên kết quả tìm kiếm để theo dõi biến động giá</p>
                        </div>
                    ) : (
                        watches.map((item) => (
                            <PriceWatchCard
                                key={item.id}
                                item={item}
                                onBuy={() => handleBuy(item)}
                                onEdit={() => setEditingItem(item)}
                                onRemove={() => setConfirmDeleteId(item.id)}
                            />
                        ))
                    )}

                    {/* Guide section — luôn hiện ở cuối */}
                    {!isLoading && <PriceWatchGuideSection />}
                </div>
            </div>

            {/* Edit alert modal */}
            {editingItem && (
                <PriceWatchAlertModal
                    visible
                    currentPrice={editingItem.lastKnownPrice}
                    confirmLabel="Cập nhật"
                    onConfirm={handleEditConfirm}
                    onCancel={() => setEditingItem(null)}
                />
            )}

            {/* Delete confirm dialog */}
            {confirmDeleteId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
                    <div className="bg-surface rounded-2xl p-6 w-full max-w-xs flex flex-col gap-4 shadow-xl">
                        <p className="text-sm font-bold text-center">Bỏ theo dõi sản phẩm này?</p>
                        <p className="text-xs text-content-3 text-center">Bạn có thể thêm lại bất cứ lúc nào.</p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="flex-1 py-2.5 border border-line rounded-xl text-sm font-semibold text-content-2"
                            >
                                Huỷ
                            </button>
                            <button
                                onClick={() => handleRemove(confirmDeleteId)}
                                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold"
                            >
                                Bỏ theo dõi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PriceWatch;
